import { TTypeOfCodec } from "../../../shared/src/codecs/codec";
import { array, TArrayCodec } from "../../../shared/src/codecs/types/array";
import { dateTime, TAnyDateTimeCodec } from "../../../shared/src/codecs/types/dateTime";
import { form, TFormCodec } from "../../../shared/src/codecs/types/form";
import { intersection, TIntersectionCodec } from "../../../shared/src/codecs/types/intersection";
import { literal } from "../../../shared/src/codecs/types/literal";
import { integer } from "../../../shared/src/codecs/types/integer";
import { nullCodec, TNullCodec } from "../../../shared/src/codecs/types/nullCodec";
import { required, TRequiredCodec } from "../../../shared/src/codecs/types/required";
import { string, TAnyStringCodec } from "../../../shared/src/codecs/types/string";
import { TUnionCodec, union } from "../../../shared/src/codecs/types/union";
import { TUuidCodec, uuid } from "../../../shared/src/codecs/types/uuid";
import { TUuidObjectCodec, UuidObject } from "../Id";
import { Pagination1, Pagination3, TPagination3Codec } from "../Pagination";
import { Address5 } from "../Address";
import { UserName, UserNames2 } from "../overloads/UserName";
import { boolean } from "../../../shared/src/codecs/types/boolean";
import { PlainUser, PlainUserEmpty, StaffUser2, TPlainUserEmptyCodec, TStaffUser2Codec } from "../User";
import { requireExhaustive } from "../../../shared/src/util";
import { ChatsMessageForm } from "./ChatsMessageForm";
import { TChatsMessageFormCodec } from "./ChatsMessageForm";
import { EmptyObject } from "../EmptyObject";
import { ChecklistItemResolvedByParty, TChecklistItemResolvedByPartyCodec } from "../ChecklistItemResolvedByParty";

export const TrueRoadblockType = union([
    literal("ad-hoc"),
    literal("client_verification"),
    literal("contracts"),
    literal("quote"),
    literal("chase_instruction"),
    literal("ongoing_risk_assessment"),
    literal("title_checks"),
    literal("contract_ready"),
    literal("exchange_ready"),
    literal("pre_completion_ready"),
    literal("completion_ready"),
    literal("post_completion"),
]);
export type TTrueRoadblockTypeCodec = typeof RoadblockType;
export type TTrueRoadblockType = TTypeOfCodec<TRoadblockTypeCodec>;

export const TestRoadblockType = union([
    literal("test_roadblock_1"),
    literal("test_roadblock_2"),
    literal("test_roadblock_3"),
    literal("test_roadblock_4"),
    literal("test_roadblock_5"),
    literal("test_roadblock_6"),
]);
export type TTestRoadblockTypeCodec = typeof RoadblockType;
export type TTestRoadblockType = TTypeOfCodec<TRoadblockTypeCodec>;

export const RoadblockType = union([
    TrueRoadblockType,
    TestRoadblockType
]);
export type TRoadblockTypeCodec = typeof RoadblockType;
export type TRoadblockType = TTypeOfCodec<TRoadblockTypeCodec>;

export const RoadblockChecklistItemType = union([
    literal("test_checklist_item_1"),
    literal("test_checklist_item_2"),
    literal("test_checklist_item_3"),
    literal("test_checklist_item_4"),
    literal("test_checklist_item_5"),
    literal("test_checklist_item_6"),
    literal("quote_created"),
    literal("quote_sent"),
    literal("draft_contract_created"),
    literal("draft_tr1_created"),
    literal("draft_as1_created"),
    literal("draft_contract_sent"),
    literal("os_conveyancer_details_added"),
    literal("verify_os_conveyancer"),
    literal("property_details_added"),
    literal("contract_details_added"),
    literal("estate_agent_details_added"),
    literal("add_completion_deadline_for_auction_case"),
    literal("add_os_client_details"),
    literal("case_instructed"),
    literal("client_id_checks_passed"),
    literal("client_video_calls_passed"),
    literal("client_no_vulnerability_concerns_on_call"),
    literal("email_phone_validation_check_completed"),
    literal("terms_of_engagement_agreed"),
    literal("consent_to_dual_rep"),
    literal("bank_accounts_verified"),
    literal("purchase_payment_method_set"),
    literal("all_clients_added_and_right_to_buy_or_sell_checked"),
    literal("ongoing_risk_assessment_completed"),
    literal("all_title_checks_complete"),
    literal("reason_for_transaction_set"),
    literal("ad_hoc"),
    literal("all_documents_referred_to_in_titles_obtained"),
    literal("unregistered_deeds_obtained"),
    literal("epc_received"),
    literal("arrange_new_epc"),
    literal("lpe1_received_for_all_leasehold_titles"),
    literal("fme1_received"),
    literal("ast_received"),
    literal("grant_of_probate_received"),
    literal("ta6_received"),
    literal("ta7_received"),
    literal("ta10_received"),
    literal("pif_received"),
    literal("local_authority_search_received"),
    literal("environment_search_received"),
    literal("drainage_search_received"),
    literal("searches_required_check_complete"),
    literal("oes_ordered"),
    literal("management_company_details_obtained"),
    literal("management_company_consent_obtained"),
    literal("account_numbers_received_for_all_charges"),
    literal("first_redemption_statement_received"),
    literal("all_enquiries_resolved_or_not_relevant"),
    literal("undertakings_approved_or_marked_none"),
    literal("risk_grade_green_and_recent"),
    literal("recent_last_transfer_risk_assessed"),
    literal("all_charges_and_title_restrictions_marked_added"),
    literal("search_report_uploaded"),
    literal("leasehold_report_uploaded"),
    literal("enquiries_report_uploaded"),
    literal("mortgage_report_uploaded"),
    literal("mortgage_lender_identified"),
    literal("mortgage_offer_added"),
    literal("mortgage_offer_questions_answered"),
    literal("mortgage_offer_not_expired"),
    literal("mortgage_retention_shared_with_client"),
    literal("early_repayment_charge_obtained"),
    literal("client_notified_of_early_repayment_charge"),
    literal("bank_accounts_verified_or_marked_unused"),
    literal("sdlt_return_uploaded_or_compass_referral_set"),
    literal("sdlt_declaration_signed_by_all_clients"),
    literal("sdlt_added_to_ledger"),
    literal("clients_authorised_exchange_and_complete"),
    literal("joint_ownership_held_as_answered"),
    literal("charges_have_account_numbers"),
    literal("apportionment_question_answered"),
    literal("dual_rep_lender_conflict_approved_if_required"),
    literal("dual_rep_missing_consent_obtained"),
    literal("leasehold_fee_added_to_ledger"),
    literal("land_registry_fee_added_to_ledger"),
    literal("electronic_transfer_fee_added_to_ledger"),
    literal("estate_agent_fee_added_to_ledger"),
    literal("deposit_added_to_ledger"),
    literal("draft_completion_statement_uploaded"),
    literal("bankruptcy_search_complete"),
    literal("report_sent"),
    literal("ready_for_exchange_review_complete"),
    literal("funds_requested_from_lender"),
    literal("funds_received_from_lender"),
    literal("funds_requested_from_client"),
    literal("funds_received_from_client"),
    literal("redemption_statement_for_completion_received"),
    literal("seller_solicitor_paid"),
    literal("seller_solicitor_payment_confirmed"),
    literal("proceeds_of_sale_received_from_buyer_solicitor"),
    literal("registration_signed_transfer_received"),
    literal("registration_signed_charge_received"),
    literal("registration_submitted"),
    literal("requisitions_resolved"),
    literal("cancellation_resolved"),
    literal("registration_complete"),
    literal("case_ledger_finalised"),
    literal("payments_due_chased"),
    literal("slip_added_to_cashroom"),
    literal("ledger_items_paid_out"),
    literal("sail_legal_invoice_paid"),
    literal("post_forwarded_or_shredded"),
    literal("peps_resolved"),
    literal("peps_holp_signoff"),
    literal("potential_fraud_reviewed"),
    literal("conflicts_of_interest_reviewed"),
    literal("failed_video_calls_reviewed"),
    literal("offer_accepted"),
    literal("title_report_uploaded"),
    literal("survey_status_answered"),
    literal("lpe1_ordered"),
    literal("seller_conveyancer_bank_account_verified"),
    literal("referral_fee_paid"),
    literal("searches_required_answered"),
    literal("searches_ordered"),
    literal("money_on_account_received"),
    literal("transfer_ownership_status_set_for_all_clients"),
    literal("auction_reservation_fee_answered"),
    literal("auction_pack_sent"),
    literal("gift_declaration_signed"),
    literal("buildings_insurance_received"),
    literal("divorce_document_type_selected"),
    literal("divorce_document_received"),
    literal("departing_owners_ila_or_disclaimer_complete"),
    literal("grant_applied_for"),
    literal("will_received"),
    literal("death_certificate_received"),
    literal("probate_grant_matched_on_hmcts"),
    literal("signed_contract_received"),
    literal("signed_tr1_received"),
    literal("signed_as1_received"),
    literal("signed_mortgage_deed_received"),
    literal("signed_occupier_consent_received"),
    literal("source_of_wealth_satisfied"),
    literal("retention_settled"),
]);
export type TRoadblockChecklistItemTypeCodec = typeof RoadblockChecklistItemType;
export type TRoadblockChecklistItemType = TTypeOfCodec<TRoadblockChecklistItemTypeCodec>;

// CHECKLIST ITEMS
export const RoadblockChecklistItem: TRequiredCodec<{
    id: TUuidCodec,
    roadblocks_id: TUuidCodec,
    manual_item_name: TUnionCodec<[TNullCodec, TAnyStringCodec]>,
    type: TRoadblockChecklistItemTypeCodec,
    resolved_at: TUnionCodec<[TAnyDateTimeCodec, TNullCodec]>,
    resolved_by: TUnionCodec<[TUuidCodec, TNullCodec]>,
    created_by: TUnionCodec<[TUuidCodec, TNullCodec]>,
}> = required({
    id: uuid(),
    roadblocks_id: uuid(),
    manual_item_name: union([nullCodec(), string()]),
    type: RoadblockChecklistItemType,
    resolved_at: union([dateTime(), nullCodec()]),
    resolved_by: union([uuid(), nullCodec()]),
    created_by: union([uuid(), nullCodec()]),
});
export type TRoadblockCheckListItemCodec = typeof RoadblockChecklistItem;
export type TRoadblockCheckListItem = TTypeOfCodec<TRoadblockCheckListItemCodec>;


export const RoadblockChecklistItemWithData: TIntersectionCodec<[
    TRoadblockCheckListItemCodec,
    TRequiredCodec<{
        roadblock_type: TRoadblockTypeCodec,
        created_by_user: TPlainUserEmptyCodec,
        resolved_by_user: TPlainUserEmptyCodec
    }>
]> = intersection([
    RoadblockChecklistItem,
    required({
        roadblock_type: RoadblockType,
        created_by_user: PlainUserEmpty,
        resolved_by_user: PlainUserEmpty,
    })
]);
export type TRoadblockChecklistItemWithDataCodec = typeof RoadblockChecklistItemWithData;
export type TRoadblockChecklistItemWithData = TTypeOfCodec<TRoadblockChecklistItemWithDataCodec>;


export const RoadblockCheckListItemManual: TRequiredCodec<{
    id: TUuidCodec,
    name: TAnyStringCodec,
    resolved_at: TUnionCodec<[TNullCodec, TAnyDateTimeCodec]>,
    resolved_by: TUnionCodec<[TNullCodec, TUuidCodec]>,
    resolved_by_name: TUnionCodec<[TNullCodec, TAnyStringCodec]>
}> = required({
    id: uuid(),
    name: string(),
    resolved_at: union([nullCodec(), dateTime()]),
    resolved_by: union([nullCodec(), uuid()]),
    resolved_by_name: union([nullCodec(), string()]),
});
export type TRoadblockCheckListItemManualCodec = typeof RoadblockCheckListItemManual;
export type TRoadblockCheckListItemManual = TTypeOfCodec<TRoadblockCheckListItemManualCodec>;


export const RoadblockEvidenceLinkBlock = union([
    literal("case_details"),
    literal("case_title_checks"),
    literal("charges_and_restrictions"),
    literal("case_quotes"),
    literal("case_contracts"),
]);
export type TRoadblockEvidenceLinkBlockCodec = typeof RoadblockEvidenceLinkBlock;
export type TRoadblockEvidenceLinkBlock = TTypeOfCodec<TRoadblockEvidenceLinkBlockCodec>;

export const RoadblockEvidenceLink = required({
    block: RoadblockEvidenceLinkBlock,
    section: union([nullCodec(), string()]),
});
export type TRoadblockEvidenceLinkCodec = typeof RoadblockEvidenceLink;
export type TRoadblockEvidenceLink = TTypeOfCodec<TRoadblockEvidenceLinkCodec>;

export const RoadblockEvidenceFact = required({
    label: string(),
    value: string(),
    status: union([literal("present"), literal("missing"), literal("neutral")]),
    link: union([nullCodec(), RoadblockEvidenceLink]),
});
export type TRoadblockEvidenceFactCodec = typeof RoadblockEvidenceFact;
export type TRoadblockEvidenceFact = TTypeOfCodec<TRoadblockEvidenceFactCodec>;

export const RoadblockChecklistItemAutomatic: TIntersectionCodec<[
    TRoadblockCheckListItemManualCodec,
    TRequiredCodec<{
        resolution_instruction: TAnyStringCodec,
        resolved_by_party: TChecklistItemResolvedByPartyCodec,
        evidence: TArrayCodec<TRoadblockEvidenceFactCodec>,
    }>
]> = intersection([
    RoadblockCheckListItemManual,
    required({
        resolution_instruction: string(),
        resolved_by_party: ChecklistItemResolvedByParty,
        evidence: array(RoadblockEvidenceFact),
    })
]);
export type TRoadblockCheckListItemAutomaticCodec = typeof RoadblockChecklistItemAutomatic;
export type TRoadblockCheckListItemAutomatic = TTypeOfCodec<TRoadblockCheckListItemAutomaticCodec>;


export const RoadblockCheckListItemManualForm: TFormCodec<
    TRoadblockCheckListItemManualCodec,
    TRequiredCodec<{
        roadblock_id: TAnyStringCodec,
        roadblock_type: TRoadblockTypeCodec,
        case_id: TUuidCodec
    }>
> = form(
    RoadblockCheckListItemManual,
    required({
        roadblock_id: string(),
        roadblock_type: RoadblockType,
        case_id: uuid(),
    })
);
export type TRoadblockCheckListItemManualFormCodec = typeof RoadblockCheckListItemManualForm;
export type TRoadblockCheckListItemManualForm = TTypeOfCodec<TRoadblockCheckListItemManualFormCodec>;


export const RoadblockSignOffForm = form(
    required({
        roadblock_id: uuid(),
        signed_by: uuid(),
    }),
    EmptyObject
);
export type TRoadblockSignOffFormCodec = typeof RoadblockSignOffForm;
export type TRoadblockSignOffForm = TTypeOfCodec<TRoadblockSignOffFormCodec>;
// END CHECKLIST ITEMS

export const RoadblockActionTag = union([
    literal("note"),
    literal("sign_off_invalidation"),
    literal("assignment"),
    literal("await"),
    literal("open"),
    literal("closed"),
]);
export type TRoadblockActionTagCodec = typeof RoadblockActionTag;
export type TRoadblockActionTag = TTypeOfCodec<TRoadblockActionTagCodec>;

export const ChatMessageAction = required({
    tag: literal("message"),
    action_date: dateTime(),
    action: required({
        id: uuid(),
        note_body: string(),
        created_at: dateTime(),
        created_by: union([uuid(), nullCodec()]),
        edited_at: union([dateTime(), nullCodec()]),
        pinned_at: union([dateTime(), nullCodec()]),
        pinned_by: union([uuid(), nullCodec()]),
    }),
});
export type TChatMessageActionCodec = typeof ChatMessageAction;
export type TChatMessageAction = TTypeOfCodec<TChatMessageActionCodec>;
export const isChatMessageAction = (action: TRoadblockAction): action is TChatMessageAction => action.tag === "message";

export const SignOffInvalidationAction = required({
    tag: literal("sign_off_invalidation"),
    action_date: dateTime(),
    action: required({
        id: uuid(),
        user: union([
            nullCodec(),
            UserName,
        ]),
        sign_off_invalidated_reason: string(),
        created_by: nullCodec(),
    }),
});
export type TSignOffInvalidationActionCodec = typeof SignOffInvalidationAction;
export type TSignOffInvalidationAction = TTypeOfCodec<TSignOffInvalidationActionCodec>;
export const isSignOffInvalidationAction = (action: TRoadblockAction): action is TSignOffInvalidationAction => action.tag === "sign_off_invalidation";

export const ChatAssignmentAction = required({
    tag: literal("assignment"),
    action_date: dateTime(),
    action: required({
        assigned_to_user_id: union([uuid(), nullCodec()]),
        created_at: dateTime(),
        created_by: union([uuid(), nullCodec()]),
        created_by_note_id: union([uuid(), nullCodec()]),
    }),
});
export type TChatAssignmentActionCodec = typeof ChatAssignmentAction;
export type TChatAssignmentAction = TTypeOfCodec<TChatAssignmentActionCodec>;
export const isChatAssignmentAction = (action: TRoadblockAction): action is TChatAssignmentAction => action.tag === "assignment";

export const ChatAwaitAction = required({
    tag: literal("await"),
    action_date: dateTime(),
    action: required({
        blocked_until: dateTime(),
        blocked_by: string(),
        cancelled_at: union([nullCodec(), dateTime()]),
        cancelled_by: union([uuid(), nullCodec()]),
        cancelled_by_note_id: union([uuid(), nullCodec()]),
        created_at: dateTime(),
        created_by: union([uuid(), nullCodec()]),
        created_by_note_id: union([uuid(), nullCodec()]),
    }),
});
export type TChatAwaitActionCodec = typeof ChatAwaitAction;
export type TChatAwaitAction = TTypeOfCodec<TChatAwaitActionCodec>;
export const isChatAwaitAction = (action: TRoadblockAction): action is TChatAwaitAction => action.tag === "await";

export const RoadblockClosedType = union([
    nullCodec(),
    literal("resolved"),
    literal("suspended")
]);

const OpenClosedActionDetails = required({
    starts_at: dateTime(),
    closed_at: union([dateTime(), nullCodec()]),
    closed_by: union([uuid(), nullCodec()]),
    closed_by_note_id: union([uuid(), nullCodec()]),
    created_at: dateTime(),
    created_by: union([uuid(), nullCodec()]),
    created_by_note_id: union([uuid(), nullCodec()]),
    closed_type: RoadblockClosedType
})

export const RoadblockOpenAction = required({
    tag: literal("open"),
    action_date: dateTime(),
    action: OpenClosedActionDetails,
});
export type TRoadblockOpenActionCodec = typeof RoadblockOpenAction;
export type TRoadblockOpenAction = TTypeOfCodec<TRoadblockOpenActionCodec>;
export const isRoadblockOpenAction = (action: TRoadblockAction): action is TRoadblockOpenAction => action.tag === "open";

export const RoadblockClosedAction = required({
    tag: literal("closed"),
    action_date: dateTime(),
    action: OpenClosedActionDetails,
});
export type TRoadblockClosedActionCodec = typeof RoadblockClosedAction;
export type TRoadblockClosedAction = TTypeOfCodec<TRoadblockClosedActionCodec>;
export const isRoadblockClosedAction = (action: TRoadblockAction): action is TRoadblockClosedAction => action.tag === "closed";
export const isRoadblockResolvedAction = (action: TRoadblockAction): action is TRoadblockClosedAction => 
    action.tag === "closed" && action.action.closed_type === "resolved"
;

export const RoadblockCheckItemAction = required({
    tag: literal("check-item"),
    action_date: dateTime(),
    action: RoadblockChecklistItem,
});
export type TRoadblockCheckItemActionCodec = typeof RoadblockCheckItemAction;
export type TRoadblockCheckItemAction = TTypeOfCodec<TRoadblockCheckItemActionCodec>;
export const isRoadblockCheckItemAction = (action: TRoadblockAction): action is TRoadblockCheckItemAction => action.tag === "check-item";

export const RoadblockAction = union([
    ChatMessageAction,
    SignOffInvalidationAction,
    ChatAssignmentAction,
    ChatAwaitAction,
    RoadblockOpenAction,
    RoadblockClosedAction,
    RoadblockCheckItemAction,
]);
export type TRoadblockActionCodec = typeof RoadblockAction;
export type TRoadblockAction = TTypeOfCodec<TRoadblockActionCodec>;

export const RoadblockStatus = union([
    literal("open"),
    literal("blocked"),
    literal("resolved"),
    literal("awaiting_signoff"),
    literal("suspended")
]);
export type TRoadblockStatusCodec = typeof RoadblockStatus;
export type TRoadblockStatus = TTypeOfCodec<TRoadblockStatusCodec>;
export const RoadblockStatus_displayString = (p: TRoadblockStatus): string =>
    p === "open" ? "Open"
    : p === "blocked" ? "Blocked"
    : p === "suspended" ? "Suspended"
    : p === "resolved" ? "Resolved"
    : p === "awaiting_signoff" ? "Awaiting Signoff"
    : requireExhaustive(p);

export const RoadblockDetails = intersection([
    required({
        case_id: uuid(),
        chats_id: uuid(),
        parent_roadblock_id: union([uuid(), nullCodec()]),
        roadblock_type: RoadblockType,
        created_at: dateTime(),
        created_by: union([uuid(), nullCodec()]),
        status: RoadblockStatus,
        sla: dateTime(),
        blocked_till: union([nullCodec(), dateTime()]),
        blocked_by_user: union([nullCodec(), UserName]),
        blocked_waiting_on: string(),
        blocked_waiting_on_is_internal: boolean(),
        resolved_by_user: union([nullCodec(), UserName]),
        resolved_on: union([nullCodec(), dateTime()]),
    }),
]);
export type TRoadblockDetailsCodec = typeof RoadblockDetails;
export type TRoadblockDetails = TTypeOfCodec<TRoadblockDetailsCodec>;

export const RoadblockAwaitableEntityCategory = union([
    literal("our_side"),
    literal("client_side"),
    literal("other_side"),
    literal("third_party"),
]);
export type TRoadblockAwaitableEntityCategoryCodec = typeof RoadblockAwaitableEntityCategory;
export type TRoadblockAwaitableEntityCategory = TTypeOfCodec<TRoadblockAwaitableEntityCategoryCodec>;
export const RoadblockAwaitableEntityCategory_displayString = (p: TRoadblockAwaitableEntityCategory): string =>
    p === "our_side" ? "Our side"
    : p === "client_side" ? "Client side"
    : p === "other_side" ? "Other side"
    : p === "third_party" ? "Third parties"
    : requireExhaustive(p);

export const RoadblockAwaitableEntityCategory_order: Array<TRoadblockAwaitableEntityCategory> = [
    "our_side",
    "client_side",
    "other_side",
    "third_party",
];

export const RoadblockAwaitableEntity = required({
    id: uuid(),
    created_at: dateTime(),
    blocked_until_days: integer(),
    name: string(),
    is_internal: boolean(),
    category: RoadblockAwaitableEntityCategory,
    sort_order: integer(),
    aliases: array(string()),
});
export type TRoadblockAwaitableEntityCodec = typeof RoadblockAwaitableEntity;
export type TRoadblockAwaitableEntity = TTypeOfCodec<TRoadblockAwaitableEntityCodec>;


export const RoadblockForm: TFormCodec<
    TUuidObjectCodec, 
    TRequiredCodec<{
        actions: TArrayCodec<TRoadblockActionCodec>,
        details: TRoadblockDetailsCodec,
        participants: TArrayCodec<TStaffUser2Codec>,
        awaitable_entities: TArrayCodec<TRoadblockAwaitableEntityCodec>,
        resolve_validation_errors: TArrayCodec<TArrayCodec<TAnyStringCodec>>,
        
        checklist_items_automatic: TArrayCodec<TRoadblockCheckListItemAutomaticCodec>,
        checklist_items_manual: TArrayCodec<TRoadblockCheckListItemManualFormCodec>,

        signoff_form: TRoadblockSignOffFormCodec,
        chat_message_form: TChatsMessageFormCodec,
    }>
> = form(
    UuidObject,
    required({
        actions: array(RoadblockAction),
        details: RoadblockDetails,
        participants: array(StaffUser2),
        awaitable_entities: array(RoadblockAwaitableEntity),
        resolve_validation_errors: array(array(string())),
        
        checklist_items_automatic: array(RoadblockChecklistItemAutomatic),        
        checklist_items_manual: array(RoadblockCheckListItemManualForm),

        signoff_form: RoadblockSignOffForm,
        chat_message_form: ChatsMessageForm,
    }),
);
export type TRoadblockFormCodec = typeof RoadblockForm;
export type TRoadblockForm = TTypeOfCodec<TRoadblockFormCodec>;

export const RoadblocksFilters = intersection([
    required({
        assignedToUserId: union([nullCodec(), uuid()]),
        actionableStatus: union([literal("actionable"), literal("blocked")]),
        roadblockType: union([nullCodec(), RoadblockType])
    }),
    Pagination1
]);
export type TRoadblocksFiltersCodec = typeof RoadblocksFilters;
export type TRoadblocksFilters = TTypeOfCodec<TRoadblocksFiltersCodec>;

export const RoadblocksListItem = intersection([
    required({
        id: uuid(),
        type: RoadblockType,
        clients: UserNames2,
        case_address: Address5,
        case_id: uuid(),
        status: RoadblockStatus
    }),
])
export type TRoadblocksListItemCodec = typeof RoadblocksListItem;
export type TRoadblocksListItem = TTypeOfCodec<TRoadblocksListItemCodec>;


export const RoadblocksForm: TFormCodec<
    TRoadblocksFiltersCodec,
    TRequiredCodec<{
        counts: TPagination3Codec;
        roadblocks: TArrayCodec<TRoadblocksListItemCodec>;
    }>
> = form(
    RoadblocksFilters,
    required({
        counts: Pagination3,
        roadblocks: array(RoadblocksListItem),
    })
);
export type TRoadblocksFormCodec = typeof RoadblocksForm;
export type TRoadblocksForm = TTypeOfCodec<TRoadblocksFormCodec>;

export const RoadblocksListItem2 =
    required({
        id: uuid(),
        type: RoadblockType,
        status: RoadblockStatus,
        sla: dateTime(),
        opened_on: dateTime(),
        last_closed_on: union([dateTime(), nullCodec()]),
        blocked_till: union([dateTime(), nullCodec()]),
        blocked_waiting_on: union([string(), nullCodec()]),
        blocked_waiting_on_is_internal: boolean(),
        assigned_users: array(PlainUser),
        detailed_roadblock_form: RoadblockForm,
    });
export type TRoadblocksListItem2Codec = typeof RoadblocksListItem2;
export type TRoadblocksListItem2 = TTypeOfCodec<TRoadblocksListItem2Codec>;

export const RoadblocksFilters2 =
    required({
        case_id: uuid(),
        assigned_to_me: boolean(),
    });
export type TRoadblocksFilters2Codec = typeof RoadblocksFilters2;
export type TRoadblocksFilters2 = TTypeOfCodec<TRoadblocksFilters2Codec>;

export const RoadblocksForm2: TFormCodec<
    TRoadblocksFilters2Codec,
    TRequiredCodec<{
        roadblocks: TArrayCodec<TRoadblocksListItem2Codec>,
        assignable_users: TArrayCodec<typeof PlainUser>,
        awaitable_entities: TArrayCodec<TRoadblockAwaitableEntityCodec>,
    }>
> = form(
    RoadblocksFilters2,
    required({
        roadblocks: array(RoadblocksListItem2),
        assignable_users: array(PlainUser),
        awaitable_entities: array(RoadblockAwaitableEntity),
    }),
);
export type TRoadblocksForm2Codec = typeof RoadblocksForm2;
export type TRoadblocksForm2 = TTypeOfCodec<TRoadblocksForm2Codec>;

export const RoadblockAssignForm = form(
    required({
        roadblock_id: uuid(),
        assigned_to_user_id: uuid(),
    }),
    required({}),
);
export type TRoadblockAssignFormCodec = typeof RoadblockAssignForm;
export type TRoadblockAssignForm = TTypeOfCodec<TRoadblockAssignFormCodec>;

export const RoadblockAwaitForm = form(
    required({
        roadblock_id: uuid(),
        awaitable_entities_id: string(),
        days_override: union([nullCodec(), integer()]),
    }),
    required({}),
);
export type TRoadblockAwaitFormCodec = typeof RoadblockAwaitForm;
export type TRoadblockAwaitForm = TTypeOfCodec<TRoadblockAwaitFormCodec>;

export const RoadblockCancelAwaitForm = form(
    required({
        roadblock_id: uuid(),
    }),
    required({}),
);
export type TRoadblockCancelAwaitFormCodec = typeof RoadblockCancelAwaitForm;
export type TRoadblockCancelAwaitForm = TTypeOfCodec<TRoadblockCancelAwaitFormCodec>;
